import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, ListSubheader, Menu, MenuItem, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { getTrainingPartPreviewPreflight } from 'http/moodle';
import { getBaseApiUrl, resolveApiErrorMessage } from 'utils';
import { getLanguageDisplayName } from 'pages/Templates/helpers/getLanguageDisplayName';

const TrainingPartPreviewLanguageMenu = ({ anchorEl, trainingPartId, onClose }) => {
  const { t, i18n } = useTranslation();
  const [preflight, setPreflight] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl && trainingPartId);

  useEffect(() => {
    let active = true;
    if (!open) {
      setPreflight(null);
      setErrorMessage('');
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setPreflight(null);
    setErrorMessage('');
    getTrainingPartPreviewPreflight(trainingPartId).then(result => {
      if (!active) {
        return;
      }
      setLoading(false);
      if (result?.error || result?.statusCode) {
        setErrorMessage(
          resolveApiErrorMessage(result.message, {
            t,
            fallbackMessage: t('trainingParts.previewLanguagesLoadFailed', 'Could not load content languages.'),
          }),
        );
        return;
      }
      setPreflight(result);
    });

    return () => {
      active = false;
    };
  }, [trainingPartId, open, t]);

  const languages = useMemo(() => (Array.isArray(preflight?.languages) ? preflight.languages : []), [preflight]);

  const handleLanguageClick = language => {
    if (!language.isAvailable) {
      return;
    }
    const baseURL = getBaseApiUrl();
    const query = new URLSearchParams({
      trainingPartId: String(trainingPartId),
      language: language.code,
    });
    window.open(`${baseURL}/lti/launch-training-part?${query.toString()}`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      MenuListProps={{ 'aria-label': t('trainingParts.openCourseIn', 'Open course in…') }}
    >
      <ListSubheader disableSticky>{t('trainingParts.openCourseIn', 'Open course in…')}</ListSubheader>
      {loading && (
        <MenuItem disabled>
          <CircularProgress size={18} />
          <Typography component="span" style={{ marginLeft: 8 }}>
            {t('trainingParts.loadingContentLanguages', 'Loading content languages…')}
          </Typography>
        </MenuItem>
      )}
      {!loading && errorMessage && <MenuItem disabled>{errorMessage}</MenuItem>}
      {!loading && !errorMessage && languages.length === 0 && (
        <MenuItem disabled>{t('trainingParts.noContentLanguages', 'No content languages configured')}</MenuItem>
      )}
      {!loading &&
        !errorMessage &&
        languages.map(language => {
          const displayName = getLanguageDisplayName(language.code, i18n?.language || 'en');
          return (
            <MenuItem
              key={language.code}
              disabled={!language.isAvailable}
              onClick={() => handleLanguageClick(language)}
            >
              <Typography component="span">
                {displayName}
                {language.isDefault ? ` (${t('trainingParts.defaultLanguage', 'Default')})` : ''}
                {!language.isAvailable ? ` — ${t('trainingParts.languageNotConfigured', 'Not configured')}` : ''}
              </Typography>
            </MenuItem>
          );
        })}
      <MenuItem divider onClick={onClose}>
        {t('buttons.cancel', 'Cancel')}
      </MenuItem>
    </Menu>
  );
};

TrainingPartPreviewLanguageMenu.defaultProps = {
  anchorEl: null,
  trainingPartId: null,
};

TrainingPartPreviewLanguageMenu.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  trainingPartId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default TrainingPartPreviewLanguageMenu;
